import { CircleHelp } from "lucide-react";
import React, { FC, ReactNode } from "react";

import type { Optional } from "@intelligems/ig-types";
import {
  FormControl,
  Grid,
  IconButton,
  TextField,
  TextFieldProps,
  TypographyProps,
  useTheme,
} from "@mui/material";

import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";
import { IgCustomTheme } from "./theme";

interface IgTextFieldProps extends Optional<TextFieldProps, "variant"> {
  label?: string | null;
  labelProps?: TypographyProps;
  tooltipMessage?: ReactNode;
  orientation?: "horizontal" | "vertical";
}

/**
 * @see TextField
 */
export const IgTextField: FC<IgTextFieldProps> = (props) => {
  const theme = useTheme();
  const {
    children,
    label,
    orientation = "vertical",
    labelProps,
    tooltipMessage,
    required,
    ...rest
  } = props;

  if (!label) {
    return (
      <TextField required={required} {...rest}>
        {children}
      </TextField>
    );
  }

  const renderLabelAndTooltip = (
    <Grid container alignItems="center" spacing={0.5} wrap="nowrap">
      <Grid item>
        <IgTypography {...labelProps}>
          {label}
          {required && (
            <IgTypography
              {...labelProps}
              component="span"
              color={(theme as IgCustomTheme).palette.text.tonal.error}
            >
              {" "}
              *
            </IgTypography>
          )}
        </IgTypography>
      </Grid>
      {tooltipMessage && (
        <Grid item>
          <IgTooltip title={tooltipMessage}>
            <IconButton size="small" aria-label="info">
              <CircleHelp size={18} />
            </IconButton>
          </IgTooltip>
        </Grid>
      )}
    </Grid>
  );

  return (
    <FormControl sx={{ width: "100%" }}>
      {orientation === "vertical" ? (
        <>
          {renderLabelAndTooltip}
          <TextField
            required={required}
            sx={{ ...rest.sx, marginTop: 2 }}
            {...rest}
          />
        </>
      ) : (
        <Grid container alignItems="center" spacing={2}>
          <Grid item sx={{ minWidth: "350px" }}>
            {renderLabelAndTooltip}
          </Grid>
          <Grid item xs>
            <TextField required={required} {...rest} fullWidth />
          </Grid>
        </Grid>
      )}
    </FormControl>
  );
};
